// Minimal IndexedDB wrapper + File System Access API helper for
// "Save directly to a local folder (e.g. C:\CargoDocs\)".
// Only supported in Chromium browsers (Chrome, Edge, Opera).

const DB = "cargodocs";
const STORE = "kv";

function openDB() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB, 1);
    req.onupgradeneeded = () => req.result.createObjectStore(STORE);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function idbGet(key) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, "readonly");
    const req = tx.objectStore(STORE).get(key);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function idbSet(key, value) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, "readwrite");
    tx.objectStore(STORE).put(value, key);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

async function idbDel(key) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, "readwrite");
    tx.objectStore(STORE).delete(key);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

export const isFolderSupported = () =>
  typeof window !== "undefined" && typeof window.showDirectoryPicker === "function";

export async function pickFolder() {
  if (!isFolderSupported()) throw new Error("Not supported in this browser. Use Chrome or Edge.");
  const handle = await window.showDirectoryPicker({ mode: "readwrite", startIn: "documents" });
  await idbSet("folder-handle", handle);
  await idbSet("folder-name", handle.name);
  return handle;
}

export async function getFolderName() {
  return (await idbGet("folder-name")) || null;
}

export async function forgetFolder() {
  // Also clear per-doc "saved locally" markers so old badges don't persist
  // against a different folder chosen later.
  try {
    const db = await openDB();
    await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, "readwrite");
      const store = tx.objectStore(STORE);
      const req = store.getAllKeys();
      req.onsuccess = () => {
        const keys = req.result || [];
        keys.forEach((k) => { if (typeof k === "string" && k.startsWith("saved:")) store.delete(k); });
      };
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } catch {}
  await idbDel("folder-handle");
  await idbDel("folder-name");
}

async function getStoredHandle() {
  const h = await idbGet("folder-handle");
  if (!h) return null;
  // Re-prompt for permission if needed
  let perm = await h.queryPermission({ mode: "readwrite" });
  if (perm !== "granted") perm = await h.requestPermission({ mode: "readwrite" });
  if (perm !== "granted") throw new Error("Folder permission denied");
  return h;
}

export async function saveBlobToFolder(filename, blob, subfolder) {
  const dir = await getStoredHandle();
  if (!dir) throw new Error("No folder chosen. Open Settings → Local folder.");
  let target = dir;
  if (subfolder) {
    target = await dir.getDirectoryHandle(subfolder, { create: true });
  }
  // Prevent overwriting: append (n) if exists
  let finalName = filename;
  let i = 1;
  const [base, ext] = splitExt(filename);
  // Iterate names by trying to open; ignore errors
  for (;;) {
    try {
      await target.getFileHandle(finalName);
      finalName = `${base} (${i})${ext}`;
      i += 1;
    } catch { break; }
  }
  const fileHandle = await target.getFileHandle(finalName, { create: true });
  const writable = await fileHandle.createWritable();
  await writable.write(blob);
  await writable.close();
  return finalName;
}

// --- Per-document "saved locally" tracking (dedup across manual + auto save) ---
export async function markDocSaved(docId, meta) {
  await idbSet(`saved:${docId}`, { ...(meta || {}), savedAt: new Date().toISOString() });
}
export async function isDocSaved(docId) {
  return Boolean(await idbGet(`saved:${docId}`));
}
export async function getSavedIds(docIds) {
  const results = await Promise.all(docIds.map(async (id) => [id, Boolean(await idbGet(`saved:${id}`))]));
  return Object.fromEntries(results);
}
export async function unmarkDocSaved(docId) {
  await idbDel(`saved:${docId}`);
}

// --- Auto-save preference ---
export async function getAutoSave() {
  return Boolean(await idbGet("auto-save"));
}
export async function setAutoSave(on) {
  await idbSet("auto-save", !!on);
}

// --- Hiccup metrics: record every time a network call auto-recovers via withAutoRetry ---
export async function recordHiccupRecovery() {
  const key = "hiccups";
  const list = (await idbGet(key)) || [];
  list.push(Date.now());
  // Prune older than 30 days to keep IDB small
  const cutoff = Date.now() - 30 * 86400000;
  const trimmed = list.filter((t) => t > cutoff);
  await idbSet(key, trimmed);
}

export async function getHiccupsSince(sinceMs) {
  const list = (await idbGet("hiccups")) || [];
  return list.filter((t) => t >= sinceMs).length;
}

function splitExt(name) {
  const dot = name.lastIndexOf(".");
  if (dot <= 0) return [name, ""];
  return [name.slice(0, dot), name.slice(dot)];
}
